// Live-data runtime for dashboards.
//
// The sandbox makes its own /api/v1/ontology/* fetches with the user's
// API key or identity access token, received at init time over postMessage
// (see sandbox-protocol.ts). Credentials live in module-scope state inside
// the iframe — never cross back to the parent and are never persisted.
//
// User dashboard code consumes this through a small fixed set of
// React hooks exposed in the render scope (see render-runtime.ts).
// Each hook follows the same shape: { data, error, loading }, with
// an optional `pollMs` for periodic refresh. Hooks are intentionally
// 1:1 with /api/v1/ontology endpoints — no client-side batching — so
// the agent's mental model matches the HTTP surface.

import type { TagReadResultRow } from '@atbash-labs/ontology-core'
import * as ontologyCore from '@atbash-labs/ontology-core'
import * as ontologyAgentWire from '@atbash-labs/ontology-agent-wire'
import * as ontologyPlantview from '@atbash-labs/ontology-plantview'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Vite resolves this workspace package to its TypeScript source as ESM
// (named exports); the Node test runner sees the same package as CommonJS
// and only exposes its exports through the namespace `default`.
const { alignTagReadResultsToRequest, normalizeTagReadResults } =
  (ontologyCore as { default?: typeof ontologyCore }).default ?? ontologyCore
const { normalizeJsonSchemaDateTimes, normalizeUtcIsoTimestamp } =
  (ontologyAgentWire as { default?: typeof ontologyAgentWire }).default ?? ontologyAgentWire
const { buildPlantDbQueryBody, buildPlantDbQueryUrl } =
  (ontologyPlantview as { default?: typeof ontologyPlantview }).default ?? ontologyPlantview

/* ----------------------------- Init / auth state ----------------------------- */

interface SandboxAuth {
  apiKey: string
  accessToken: string
  organizationId: string
  proxyBaseUrl: string
  branch: string
  site: string
  /** Saved dashboard id. Absent for templates, artifacts, and headless captures. */
  dashboardId?: string
}

let auth: SandboxAuth | null = null
const authListeners = new Set<() => void>()

const revisionCounters = new Map<string, number>()
const revisionListeners = new Map<string, Set<() => void>>()

/** Scope {@link bumpDataRevision} uses to refetch {@link useAgentFindings}. */
export const AGENT_FINDINGS_REVISION_SCOPE = 'agent-findings'

export function setSandboxAuth(next: SandboxAuth): void {
  auth = next
  for (const listener of authListeners) listener()
}

export function getSandboxAuth(): SandboxAuth | null {
  return auth
}

function subscribeToAuth(listener: () => void): () => void {
  authListeners.add(listener)
  return () => {
    authListeners.delete(listener)
  }
}

/**
 * Bump a named data-revision so hooks subscribed to that scope refetch
 * in the background (keep existing `data`, do not set `loading`).
 * Call only from event handlers — a bump during render re-enters the
 * fetch forever. {@link useAgentFindings} listens on
 * {@link AGENT_FINDINGS_REVISION_SCOPE}.
 */
export function bumpDataRevision(scope: string): void {
  revisionCounters.set(scope, (revisionCounters.get(scope) ?? 0) + 1)
  const listeners = revisionListeners.get(scope)
  if (!listeners) return
  for (const listener of listeners) listener()
}

/** Test/host subscriber for {@link bumpDataRevision}. Not in dashboard SCOPE. */
export function subscribeToDataRevision(scope: string, listener: () => void): () => void {
  let listeners = revisionListeners.get(scope)
  if (!listeners) {
    listeners = new Set()
    revisionListeners.set(scope, listeners)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) revisionListeners.delete(scope)
  }
}

function useRevision(scope: string | undefined): number {
  const [revision, setRevision] = useState(() => (scope ? (revisionCounters.get(scope) ?? 0) : 0))
  useEffect(() => {
    if (!scope) return
    setRevision(revisionCounters.get(scope) ?? 0)
    return subscribeToDataRevision(scope, () => {
      setRevision(revisionCounters.get(scope) ?? 0)
    })
  }, [scope])
  return revision
}

/* ----------------------------- HTTP plumbing -------------------------------- */

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const base = auth?.proxyBaseUrl ?? ''
  // Empty base → same-origin (dev: Vite proxies /api to the Go proxy).
  // Non-empty → absolute prod URL.
  let url = `${base}/api/v1/ontology${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    const str = String(value)
    if (str === '') continue
    params.set(key, str)
  }
  const qs = params.toString()
  if (qs) url += `?${qs}`
  return url
}

/** Like {@link buildUrl} but for proxy surfaces outside `/api/v1/ontology` (e.g. `/api/v1/custom-agents`). */
function buildV1Url(path: string, query?: Record<string, unknown>): string {
  const base = auth?.proxyBaseUrl ?? ''
  let url = `${base}/api/v1${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    const str = String(value)
    if (str === '') continue
    params.set(key, str)
  }
  const qs = params.toString()
  if (qs) url += `?${qs}`
  return url
}

export function buildSandboxHeaders(): Headers {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  if (!auth) return headers
  // Prefer the API-key path, including the synthesized demo key. The proxy
  // gives Authorization absolute precedence when both headers are present,
  // which would otherwise turn an API-key session into a short-lived JWT one.
  if (auth.apiKey) headers.set('X-API-Key', auth.apiKey)
  else if (auth.accessToken) headers.set('Authorization', `Bearer ${auth.accessToken}`)
  if (auth.organizationId) headers.set('X-Organization-ID', auth.organizationId)
  if (auth.branch) headers.set('X-Ontology-Branch', auth.branch)
  if (auth.site) headers.set('X-Ontology-Site', auth.site)
  return headers
}

async function requestJson(url: string, init: RequestInit, signal: AbortSignal): Promise<unknown> {
  if (auth?.apiKey === 'demo') {
    return demoJson(url, init)
  }

  const response = await fetch(url, {
    ...init,
    signal,
    // Defensive: cross-origin cookie credentials are NOT needed here —
    // auth rides in explicit headers, not cookies — and combining
    // `credentials: 'include'` with the proxy's wildcard
    // Access-Control-Allow-Origin would be rejected by the browser.
    credentials: 'omit',
    headers: buildSandboxHeaders()
  })
  const text = await response.text()
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`.trim()
    if (text) {
      try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed === 'object' && 'message' in parsed) {
          message = String((parsed as Record<string, unknown>).message ?? message)
        } else {
          message = text.slice(0, 500)
        }
      } catch {
        message = text.slice(0, 500)
      }
    }
    throw new Error(message)
  }
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function demoNodeIds(init: RequestInit): string[] {
  if (typeof init.body !== 'string') return []
  try {
    const body = JSON.parse(init.body) as { node_ids?: unknown }
    return Array.isArray(body.node_ids)
      ? body.node_ids.filter((nodeId): nodeId is string => typeof nodeId === 'string')
      : []
  } catch {
    return []
  }
}

function demoJson(url: string, init: RequestInit): unknown {
  const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
  const parsed = new URL(url, origin)
  if (parsed.pathname.endsWith('/tags/read')) {
    return {
      results: demoNodeIds(init).map((nodeId, index) => ({
        node_id: nodeId,
        tag_path: `Demo/Tag${index + 1}`,
        value: 71.4 + index,
        quality: 'Good',
        is_good: true,
        timestamp: new Date().toISOString(),
        source: 'subscription',
        stale: false
      }))
    }
  }
  if (parsed.pathname.endsWith('/tags/history')) {
    return {
      results: demoNodeIds(init).map((nodeId, index) => ({
        node_id: nodeId,
        tag_path: `Demo/Tag${index + 1}`,
        data: [3, 2, 1].map((hoursAgo) => ({
          t_stamp: new Date(Date.now() - hoursAgo * 3600e3).toISOString(),
          value: 70 + index + hoursAgo
        })),
        count: 3,
        error: null
      }))
    }
  }
  if (parsed.pathname.endsWith('/cypher/read')) {
    return {
      count: 2,
      total: 2,
      returned: 2,
      offset: 0,
      results: [
        { lineId: 'urn:demo:line:1', lineName: 'Line 1', equipmentCount: 4 },
        { lineId: 'urn:demo:line:2', lineName: 'Line 2', equipmentCount: 6 }
      ]
    }
  }
  if (parsed.pathname.endsWith('/live-data/tags')) {
    return {
      tags: [
        {
          path: 'Line2/Filler02/FillLevel_StdDev',
          value: 4.8,
          quality: 'good',
          timestamp: new Date().toISOString(),
          unit: 'ml'
        },
        {
          path: 'Line2/OEE',
          value: 71.4,
          quality: 'good',
          timestamp: new Date().toISOString(),
          unit: '%'
        }
      ]
    }
  }
  if (parsed.pathname.endsWith('/live-data/alarms/active')) {
    return { alarms: [{ name: 'Fill variance high', priority: 'High', state: 'Active' }] }
  }
  if (parsed.pathname.endsWith('/live-data/alarms/status')) {
    return { status: 'ok', active: 1, shelved: 0 }
  }
  if (parsed.pathname.endsWith('/live-data/gateway-status')) {
    return { status: 'connected', gateway: 'Ignition Gateway A' }
  }
  if (parsed.pathname.endsWith(':evaluate')) {
    return {
      name: 'powerAnalysisConfigs/demo',
      standard: 'ieee-519-2022',
      verdict: 'compliant',
      metrics: [
        {
          metric: 'voltage_thd',
          verdict: 'compliant',
          value: 3.1,
          limit: 8,
          unit: '%',
          margin: 4.9,
          rationale: 'Voltage THD 3.10% ≤ 8.00% limit (nominal ≤ 1 kV).'
        },
        {
          metric: 'current_tdd',
          verdict: 'compliant',
          value: 4.2,
          limit: 5,
          unit: '%',
          margin: 0.8,
          rationale: 'Current TDD 4.20% ≤ 5.00% limit (Isc/IL = 32).'
        }
      ],
      inputs: [
        { input: 'voltage_thd', unit: '%', value: 3.1, provenance: 'tag last value' },
        { input: 'nominal_voltage', unit: 'V', value: 480, provenance: 'constant' }
      ]
    }
  }
  if (parsed.pathname.endsWith(':resolve')) {
    return {
      name: 'powerAnalysisConfigs/demo',
      standard: 'ieee-519-2022',
      inputs: [
        { input: 'voltage_thd', unit: '%', value: 3.1, provenance: 'tag last value' },
        { input: 'nominal_voltage', unit: 'V', value: 480, provenance: 'constant' }
      ]
    }
  }
  if (parsed.pathname.includes('/custom-agents/configs')) {
    if (parsed.pathname.endsWith('/results')) {
      if (parsed.searchParams.get('view') === 'items') {
        const sharedRunId = 'demo-run-shared'
        const t1 = new Date(Date.now() - 3600e3).toISOString()
        const t2 = new Date(Date.now() - 2 * 3600e3).toISOString()
        return {
          results: [
            {
              id: 'demo-finding-1',
              time: t1,
              run_id: sharedRunId,
              type: 'action_items',
              position: 0,
              result: { title: 'Check infeed photoeye', priority: 'medium' },
              approval_status: 'awaiting_review'
            },
            {
              id: 'demo-finding-2',
              time: t1,
              run_id: sharedRunId,
              type: 'action_items',
              position: 1,
              result: { title: 'Replace worn belt', priority: 'high' },
              approval_status: 'approved'
            },
            {
              id: 'demo-finding-3',
              time: t2,
              run_id: 'demo-run-other',
              type: 'root_causes',
              position: 0,
              result: { title: 'Jam cleared itself', confidence: 0.4 },
              approval_status: 'rejected',
              feedback: 'Not a lasting fault',
              reviewed_at: t2
            }
          ],
          next_page_token: ''
        }
      }
      return {
        results: [
          {
            time: new Date(Date.now() - 3600e3).toISOString(),
            run_id: 'demo-run-1',
            result: { oee: 71.4, line: 'Line 2' }
          },
          {
            time: new Date(Date.now() - 2 * 3600e3).toISOString(),
            run_id: 'demo-run-2',
            result: { oee: 74.1, line: 'Line 2' }
          }
        ],
        next_page_token: ''
      }
    }
    return {
      custom_agent_configs: [
        {
          id: 'demo-config-1',
          name: 'oee-watcher',
          usage_description: 'Hourly line OEE snapshot',
          state: 'active',
          output_schema: {
            type: 'object',
            properties: {
              oee: { type: 'number', 'x-reviewable': false },
              action_items: { type: 'array', 'x-reviewable': true }
            }
          }
        }
      ]
    }
  }
  // Single-item review PATCH. Distinct from `/configs/{id}/results` (list).
  if (parsed.pathname.includes('/custom-agents/results/')) {
    const id = decodeURIComponent(parsed.pathname.split('/').pop() ?? 'demo-finding-1')
    let approvalStatus = 'approved'
    let feedback: string | undefined
    if (typeof init.body === 'string') {
      try {
        const body = JSON.parse(init.body) as Record<string, unknown>
        const rawStatus = body.approval_status ?? body.approvalStatus
        if (
          rawStatus === 'approved' ||
          rawStatus === 'rejected' ||
          rawStatus === 'awaiting_review'
        ) {
          approvalStatus = rawStatus
        }
        if (typeof body.feedback === 'string' && body.feedback !== '') {
          feedback = body.feedback
        }
      } catch {
        // keep defaults
      }
    }
    const now = new Date().toISOString()
    return {
      id,
      time: now,
      run_id: 'demo-run-shared',
      type: 'action_items',
      position: 0,
      result: { title: 'Demo finding' },
      approval_status: approvalStatus,
      ...(feedback ? { feedback } : {}),
      reviewed_at: now
    }
  }
  // Connection-addressed DB plane (P4): registry list + /{id}/query.
  if (parsed.pathname.endsWith('/db/connections')) {
    return {
      connections: [
        {
          connection_id: 'urn:db:Historian',
          name: 'Historian',
          transport: 'webdev',
          engine: 'postgres',
          capabilities: ['webdev_query']
        }
      ]
    }
  }
  if (parsed.pathname.includes('/db/connections/') && parsed.pathname.endsWith('/query')) {
    return {
      connection_id: 'urn:db:Historian',
      success: true,
      result: [{ metric: 'Line 2 OEE', value: 71.4 }]
    }
  }
  if (parsed.pathname.endsWith('/soap/endpoints')) {
    return {
      endpoints: [
        {
          endpoint_id: 'urn:soap:demo',
          contract_hash: 'demo-contract',
          enabled_operations: ['GetProductionSummary']
        }
      ]
    }
  }
  if (parsed.pathname.endsWith('/soap/invoke')) {
    return {
      endpoint_id: 'urn:soap:demo',
      contract_hash: 'demo-contract',
      operation_key: 'GetProductionSummary',
      result: { line: 'Line 2', produced: 1240, target: 1300 }
    }
  }
  return {}
}

/* ------------------------------- Hook helpers ------------------------------- */

export interface HookState<T> {
  data: T | null
  error: string | null
  loading: boolean
}

export type TagWriteValue = number | string | boolean

export interface TagWriteInput {
  /** Graph tag node ID accepted by the proxy writeback service. */
  targetId: string
  value: TagWriteValue
  /** Optional caller key. One is generated once per invocation when omitted. */
  idempotencyKey?: string
}

export interface TagWriteState<T = unknown> {
  /** Invoke only from an explicit operator event handler. Never call during render or an effect. */
  write: (input: TagWriteInput) => Promise<T>
  pending: boolean
  error: string | null
  result: T | null
}

interface QueryHookOptions<TParams> {
  request: (params: TParams, signal: AbortSignal) => Promise<unknown>
  params: TParams
  pollMs?: number
  /**
   * Delay before the first fetch (and before starting `pollMs`). Used to
   * stagger concurrent gateway DB queries so Ignition connections are not
   * flooded when a dashboard mounts many `useDbQuery` hooks at once.
   */
  delayMs?: number
  /**
   * Wait this long after a failed fetch before retrying. `0` / omitted =
   * no retry. `useDbQuery` defaults to 5s to ride out Ignition
   * "too many concurrent queries" errors.
   */
  retryMs?: number
  /** Max retries after the initial attempt (default 8 when `retryMs` > 0). */
  maxRetries?: number
  /**
   * If false, skip the fetch entirely. Used to gate hooks on required
   * inputs being present (e.g. an empty gateway URL).
   */
  enabled?: boolean
  /**
   * Hash of `params` used as the dependency for re-fetching. We hash
   * rather than depending on the object directly so a parent re-render
   * with structurally-equal params doesn't kick off a new request.
   */
  paramsKey: string
  /**
   * When set, a {@link bumpDataRevision} for this scope refetches this
   * hook in the background (same as a poll tick: keep `data`, do not
   * set `loading`). Scoped so a findings review does not restart
   * staggered `useDbQuery` gateway calls or blank the dashboard.
   */
  revisionScope?: string
}

function useAuthReady(): boolean {
  const [ready, setReady] = useState<boolean>(() => getSandboxAuth() !== null)
  useEffect(() => {
    if (ready) return
    return subscribeToAuth(() => setReady(getSandboxAuth() !== null))
  }, [ready])
  return ready
}

function useDataQuery<T, TParams>(options: QueryHookOptions<TParams>): HookState<T> {
  const {
    request,
    params,
    paramsKey,
    pollMs,
    delayMs = 0,
    retryMs = 0,
    maxRetries = retryMs > 0 ? 8 : 0,
    enabled = true,
    revisionScope
  } = options
  const authReady = useAuthReady()
  const revision = useRevision(revisionScope)
  const effectiveEnabled = enabled && authReady

  const [state, setState] = useState<HookState<T>>({
    data: null,
    error: null,
    loading: effectiveEnabled
  })

  // Keep latest in refs so the polling effect can read them without
  // restarting the interval on every render.
  const latest = useRef({ request, params })
  latest.current = { request, params }
  const refreshRef = useRef<(() => void) | null>(null)
  const prevRevision = useRef(revision)

  useEffect(() => {
    if (!effectiveEnabled) {
      setState({ data: null, error: null, loading: false })
      return
    }
    const controller = new AbortController()
    let cancelled = false
    let intervalHandle: number | undefined
    let delayHandle: number | undefined
    let retryHandle: number | undefined

    const clearRetry = () => {
      if (retryHandle !== undefined) {
        window.clearTimeout(retryHandle)
        retryHandle = undefined
      }
    }

    const run = async (attempt = 0) => {
      clearRetry()
      try {
        const data = (await latest.current.request(latest.current.params, controller.signal)) as T
        if (cancelled) return
        setState({ data, error: null, loading: false })
      } catch (err) {
        if (cancelled) return
        if (err instanceof Error && err.name === 'AbortError') return
        const message = err instanceof Error ? err.message : String(err)
        const canRetry = retryMs > 0 && attempt < maxRetries
        if (canRetry) {
          // Keep loading true while waiting to retry (shows spinner, not a hard fail).
          setState((prev) => ({ data: prev.data, error: message, loading: true }))
          retryHandle = window.setTimeout(() => {
            if (cancelled) return
            void run(attempt + 1)
          }, retryMs)
          return
        }
        setState((prev) => ({ data: prev.data, error: message, loading: false }))
      }
    }

    const start = () => {
      if (cancelled) return
      setState((prev) => ({ data: prev.data, error: null, loading: true }))
      void run(0)
      if (pollMs && pollMs > 0) {
        // Skip ticks while a failure backoff is pending — otherwise clearRetry()
        // inside run(0) would cancel the retry and reset attempt counting.
        intervalHandle = window.setInterval(() => {
          if (retryHandle !== undefined) return
          void run(0)
        }, pollMs)
      }
    }

    // Poll ticks and review-driven revision bumps call this without
    // flipping `loading`, so a dashboard gated on `loading` does not
    // unmount the whole page.
    refreshRef.current = () => {
      if (cancelled) return
      void run(0)
    }

    setState((prev) => ({ data: prev.data, error: null, loading: true }))
    if (delayMs > 0) {
      delayHandle = window.setTimeout(start, delayMs)
    } else {
      start()
    }

    return () => {
      cancelled = true
      controller.abort()
      clearRetry()
      if (delayHandle !== undefined) window.clearTimeout(delayHandle)
      if (intervalHandle !== undefined) window.clearInterval(intervalHandle)
    }
    // paramsKey and auth-readiness restart the fetch. A scoped revision
    // bump is a background refresh (see the effect below) so it does not
    // abort in-flight work or set loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, pollMs, delayMs, retryMs, maxRetries, effectiveEnabled])

  useEffect(() => {
    if (!revisionScope) return
    if (prevRevision.current === revision) return
    prevRevision.current = revision
    refreshRef.current?.()
  }, [revision, revisionScope])

  return state
}

/** Mount-order counter so concurrent `useDbQuery` hooks can stagger. */
let dbQueryMountSeq = 0

/** Odd-mounted DB queries wait this long before the first fetch (wave 2). */
const DB_QUERY_STAGGER_MS = 1500

function stableKey(value: unknown): string {
  try {
    return JSON.stringify(
      value,
      value !== null && typeof value === 'object'
        ? Object.keys(value as Record<string, unknown>).sort()
        : undefined
    )
  } catch {
    return ''
  }
}

/**
 * One registry-backed row in a `useTagValues` response.
 *
 *   - `nodeId` is the graph URN used to address the tag.
 *   - The resolved provider path always lives at `tagPath`.
 *   - `quality` / `isGood` are always present and `value` is always
 *     defined (possibly `null`) — no need to optional-chain.
 *
 * `error` only appears on failure rows (e.g. unreachable gateway, bad
 * path); when present, `isGood` is `false` and `quality` is `"Bad"`.
 */
export interface TagValue {
  nodeId: string
  tagPath: string
  value: number | string | boolean | null
  quality: string
  isGood: boolean
  tagType: string
  error?: string
  /** Acquisition timestamp as canonical UTC ISO-8601, when supplied by the registry. */
  timestamp?: string
  /** Acquisition route that supplied the value; use `tagPath` for tag identity. */
  source?: string
  /** Whether the cached value exceeded its freshness budget. */
  stale?: boolean
}

/**
 * Normalized payload for `useTagValues`. `tags` preserves request order and
 * `byNodeId` is the canonical graph-identity lookup.
 */
export interface TagValuesResult {
  tags: TagValue[]
  byNodeId: Record<string, TagValue>
}

/**
 * One historian point in tall form (the chart-friendly shape).
 *
 * Note `timestamp` is camelCase even though the proxy's wire format
 * uses `t_stamp` — we normalize so dashboard code never has to think
 * about snake-vs-camel.
 */
export interface TagHistoryPoint {
  /** Canonical UTC ISO-8601 instant ending in `Z`. */
  timestamp: string
  tagPath: string
  nodeId: string
  value: number | string | boolean | null
}

/**
 * Normalized node-ID history payload. `points` is a flat tall array and
 * `byNodeId` contains each graph-identity series sorted by timestamp.
 */
export interface TagHistoryResult {
  points: TagHistoryPoint[]
  byNodeId: Record<string, TagHistoryPoint[]>
  /** Per-node proxy failures retained alongside healthy sibling series. */
  errorsByNodeId: Record<string, unknown>
  count: number
}

interface LegacyTagHistoryPoint extends Omit<TagHistoryPoint, 'timestamp'> {
  timestamp: number
}

interface LegacyTagHistoryResult {
  points: LegacyTagHistoryPoint[]
  byNodeId: Record<string, LegacyTagHistoryPoint[]>
  errorsByNodeId: Record<string, unknown>
  count: number
}

/**
 * Runtime-only adapter for persisted dashboards authored before history
 * timestamps became canonical UTC ISO strings. This shape is deliberately not
 * exposed in Monaco declarations or builder guidance.
 */
export function toLegacyTagHistoryResult(result: TagHistoryResult): LegacyTagHistoryResult {
  const converted = new Map<TagHistoryPoint, LegacyTagHistoryPoint>()
  const convertPoint = (point: TagHistoryPoint): LegacyTagHistoryPoint => {
    const existing = converted.get(point)
    if (existing) return existing
    const legacy = { ...point, timestamp: Date.parse(point.timestamp) }
    converted.set(point, legacy)
    return legacy
  }

  return {
    points: result.points.map(convertPoint),
    byNodeId: Object.fromEntries(
      Object.entries(result.byNodeId).map(([nodeId, points]) => [nodeId, points.map(convertPoint)])
    ),
    errorsByNodeId: result.errorsByNodeId,
    count: result.count
  }
}

function coerceScalar(raw: unknown): number | string | boolean | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number' || typeof raw === 'string' || typeof raw === 'boolean') return raw
  try {
    return JSON.stringify(raw)
  } catch {
    return String(raw)
  }
}

/* --------------------- Node-ID data plane (tags/read, tags/history) --------------------- */

interface TagReadByIdParams {
  nodeIds: string[]
  fresh?: boolean
  maxStalenessMs?: number
}

function toDashboardTagValue(row: TagReadResultRow): TagValue {
  const quality = row.quality ?? 'Unknown'
  const timestamp =
    row.timestamp === undefined || row.timestamp === ''
      ? undefined
      : normalizeUtcIsoTimestamp(row.timestamp, `tag value ${row.nodeId} timestamp`, {
          epochUnit: 'milliseconds'
        })
  return {
    tagPath: row.tagPath,
    nodeId: row.nodeId,
    value: coerceScalar(row.value),
    quality,
    isGood: row.isGood ?? quality.toLowerCase() === 'good',
    tagType: row.tagType ?? '',
    stale: row.stale ?? false,
    ...(timestamp ? { timestamp } : {}),
    ...(row.source ? { source: row.source } : {}),
    ...(row.error ? { error: row.error } : {})
  }
}

async function requestTagReadById(
  p: TagReadByIdParams,
  signal: AbortSignal
): Promise<TagValuesResult> {
  const body: Record<string, unknown> = { node_ids: p.nodeIds }
  if (p.fresh) body.fresh = true
  if (typeof p.maxStalenessMs === 'number') body.max_staleness_ms = p.maxStalenessMs
  const raw = (await requestJson(
    buildUrl('/tags/read'),
    { method: 'POST', body: JSON.stringify(body) },
    signal
  )) as {
    results?: unknown
  } | null
  if (raw === null || !Array.isArray(raw.results)) {
    throw new Error('Tag read response must contain a results array.')
  }
  const tags = alignTagReadResultsToRequest(
    p.nodeIds,
    normalizeTagReadResults(raw.results).map(toDashboardTagValue),
    (nodeId) => ({
      nodeId,
      tagPath: '',
      value: null,
      quality: 'Bad',
      isGood: false,
      tagType: '',
      stale: false,
      error: 'Tag read response omitted the requested node ID.'
    })
  )
  const byNodeId: Record<string, TagValue> = {}
  for (const tag of tags) {
    byNodeId[tag.nodeId] = tag
  }
  return { tags, byNodeId }
}

interface TagHistoryByIdParams {
  nodeIds: string[]
  startDate: string
  endDate: string
  aggregationMode?: string
  returnSize?: number
  intervalMinutes?: number
  aggregateWindow?: boolean
}

/** Points a chart gets when the widget did not choose a resolution. */
const DEFAULT_HISTORY_POINTS = 100

async function requestTagHistoryById(
  p: TagHistoryByIdParams,
  signal: AbortSignal
): Promise<TagHistoryResult> {
  const body: Record<string, unknown> = {
    node_ids: p.nodeIds,
    start_date: p.startDate,
    end_date: p.endDate,
    return_format: 'Tall'
  }
  if (p.aggregationMode) body.aggregation_mode = p.aggregationMode
  if (typeof p.returnSize === 'number') body.return_size = p.returnSize
  if (typeof p.intervalMinutes === 'number') body.interval_minutes = p.intervalMinutes
  if (typeof p.aggregateWindow === 'boolean') body.aggregate_window = p.aggregateWindow
  // Charts are the reason this surface exists, so it asks for a series even
  // when the widget named no resolution. The server reads "no bucket width
  // and no point count" as a request for one aggregate value, which would
  // otherwise render every line chart as a single point.
  const askedForShape =
    p.aggregateWindow === true ||
    typeof p.returnSize === 'number' ||
    typeof p.intervalMinutes === 'number'
  if (!askedForShape) body.return_size = DEFAULT_HISTORY_POINTS
  const raw = (await requestJson(
    buildUrl('/tags/history'),
    { method: 'POST', body: JSON.stringify(body) },
    signal
  )) as { results?: unknown } | null
  if (raw === null || !Array.isArray(raw.results)) {
    throw new Error('Tag history response must contain a results array.')
  }
  const results = raw.results
  const points: TagHistoryPoint[] = []
  const byNodeId: Record<string, TagHistoryPoint[]> = {}
  const errorsByNodeId: Record<string, unknown> = {}
  let count = 0

  const consumeItem = (item: unknown): void => {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) return
    const record = item as Record<string, unknown>
    const nodeId = typeof record.node_id === 'string' ? record.node_id : ''
    if (nodeId === '') return
    if (record.error !== null && record.error !== undefined) {
      byNodeId[nodeId] = []
      errorsByNodeId[nodeId] = record.error
      return
    }
    if (Array.isArray(record.values)) {
      for (const child of record.values) consumeItem(child)
      return
    }
    const tagPath = typeof record.tag_path === 'string' ? record.tag_path : ''
    const rows = Array.isArray(record.data) ? record.data : []
    const series: TagHistoryPoint[] = []
    for (const row of rows) {
      if (typeof row !== 'object' || row === null || Array.isArray(row)) continue
      const tallRow = row as Record<string, unknown>
      const rawTimestamp = tallRow.bucket_time ?? tallRow.t_stamp ?? tallRow.timestamp
      const timestamp = normalizeUtcIsoTimestamp(rawTimestamp, `tag history ${nodeId} timestamp`, {
        epochUnit: 'milliseconds'
      })
      const point: TagHistoryPoint = {
        timestamp,
        tagPath,
        nodeId,
        value: coerceScalar(tallRow.value)
      }
      series.push(point)
      points.push(point)
    }
    series.sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
    byNodeId[nodeId] = series
    count += series.length
  }

  for (const item of results) {
    consumeItem(item)
  }

  points.sort((a, b) =>
    a.timestamp === b.timestamp
      ? a.tagPath.localeCompare(b.tagPath)
      : Date.parse(a.timestamp) - Date.parse(b.timestamp)
  )
  return { points, count, byNodeId, errorsByNodeId }
}

interface CypherQueryParams {
  query: string
  params?: Record<string, unknown>
}

/**
 * One read-only Cypher result page.
 *
 * Row keys are the aliases chosen by the query and are therefore preserved
 * exactly. Envelope metadata is normalized to camelCase. `truncated` is true
 * when the proxy reports more rows than this first page returned.
 */
export interface CypherQueryResult {
  rows: Array<Record<string, unknown>>
  columns: string[]
  count: number
  returned: number
  offset: number
  total?: number
  resultId?: string
  cachedRows?: number
  totalUnavailable?: string
  inlineTruncated: boolean
  truncated: boolean
}

async function requestCypherQuery(
  p: CypherQueryParams,
  signal: AbortSignal
): Promise<CypherQueryResult> {
  const raw = (await requestJson(
    buildUrl('/cypher/read'),
    {
      method: 'POST',
      body: JSON.stringify({ query: p.query, params: p.params ?? {} })
    },
    signal
  )) as Record<string, unknown> | null
  if (raw === null || !Array.isArray(raw.results)) {
    throw new Error('Cypher read response must contain a results array.')
  }
  const rows = raw.results.filter(
    (row): row is Record<string, unknown> =>
      typeof row === 'object' && row !== null && !Array.isArray(row)
  )
  const returned =
    typeof raw.returned === 'number' && Number.isFinite(raw.returned) ? raw.returned : rows.length
  const offset = typeof raw.offset === 'number' && Number.isFinite(raw.offset) ? raw.offset : 0
  const count =
    typeof raw.count === 'number' && Number.isFinite(raw.count) ? raw.count : rows.length
  const total = typeof raw.total === 'number' && Number.isFinite(raw.total) ? raw.total : undefined
  const cachedRows =
    typeof raw.cached_rows === 'number' && Number.isFinite(raw.cached_rows)
      ? raw.cached_rows
      : undefined
  const resultId =
    typeof raw.result_id === 'string' && raw.result_id !== '' ? raw.result_id : undefined
  const totalUnavailable =
    typeof raw.total_unavailable === 'string' && raw.total_unavailable !== ''
      ? raw.total_unavailable
      : undefined
  const inlineTruncated = raw.inline_truncated === true

  return {
    rows,
    columns: rows.length > 0 ? Object.keys(rows[0]) : [],
    count,
    returned,
    offset,
    ...(total !== undefined ? { total } : {}),
    ...(resultId !== undefined ? { resultId } : {}),
    ...(cachedRows !== undefined ? { cachedRows } : {}),
    ...(totalUnavailable !== undefined ? { totalUnavailable } : {}),
    inlineTruncated,
    truncated:
      inlineTruncated || (total !== undefined ? offset + returned < total : resultId !== undefined)
  }
}

interface ActiveAlarmsParams {
  gatewayUrl: string
  projectName?: string
  priority?: string
  state?: string
}

/**
 * Best-effort shape for one Ignition alarm event. The Ignition
 * gateway emits a permissive bag-of-properties (camelCase) — the
 * fields listed here are the ones nearly every project surfaces, but
 * the `[key: string]: unknown` index keeps any project-specific
 * fields (custom associated-data, etc.) reachable. Any field can be
 * absent or null in practice.
 */
export interface ActiveAlarm {
  eventId?: string
  source?: string
  displayPath?: string
  name?: string
  priority?: string
  state?: string
  label?: string
  notes?: string
  ackUser?: string
  /** Curated alarm instants are canonical UTC ISO-8601 strings. */
  eventTime?: string
  ackTime?: string
  clearTime?: string
  clearedTime?: string
  [key: string]: unknown
}

/**
 * Normalized payload for `useActiveAlarms`. `alarms` preserves the
 * upstream order (sorted by the gateway, usually most-recent first);
 * the two index maps make priority/state filtering a one-liner.
 *
 *   data.byPriority["Critical"]?.length ?? 0
 *   data.byState["ActiveUnacked"]?.[0]
 */
export interface ActiveAlarmsResult {
  alarms: ActiveAlarm[]
  byPriority: Record<string, ActiveAlarm[]>
  byState: Record<string, ActiveAlarm[]>
}

function indexBy<T>(items: T[], pick: (item: T) => string | undefined): Record<string, T[]> {
  const out: Record<string, T[]> = {}
  for (const item of items) {
    const key = pick(item)
    if (key === undefined || key === '') continue
    let bucket = out[key]
    if (!bucket) {
      bucket = []
      out[key] = bucket
    }
    bucket.push(item)
  }
  return out
}

async function requestActiveAlarms(
  p: ActiveAlarmsParams,
  signal: AbortSignal
): Promise<ActiveAlarmsResult> {
  const raw = (await requestJson(
    buildUrl('/live-data/alarms/active', {
      gateway_url: p.gatewayUrl,
      project: p.projectName,
      priority: p.priority,
      state: p.state
    }),
    { method: 'GET' },
    signal
  )) as { alarms?: unknown } | null
  const rawAlarms = Array.isArray(raw?.alarms) ? raw.alarms : []
  const alarms: ActiveAlarm[] = []
  for (const [index, row] of rawAlarms.entries()) {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) continue
    const alarm = { ...(row as Record<string, unknown>) }
    for (const field of ['eventTime', 'ackTime', 'clearTime', 'clearedTime'] as const) {
      if (alarm[field] !== undefined && alarm[field] !== null && alarm[field] !== '') {
        alarm[field] = normalizeUtcIsoTimestamp(alarm[field], `active alarm ${index} ${field}`, {
          epochUnit: 'milliseconds'
        })
      }
    }
    alarms.push(alarm as ActiveAlarm)
  }
  return {
    alarms,
    byPriority: indexBy(alarms, (a) =>
      typeof a.priority === 'string' && a.priority !== '' ? a.priority : undefined
    ),
    byState: indexBy(alarms, (a) =>
      typeof a.state === 'string' && a.state !== '' ? a.state : undefined
    )
  }
}

interface AlarmStatusParams {
  gatewayUrl: string
  projectName?: string
}

/**
 * One alarm pipeline as configured on the gateway. Permissive
 * `[key: string]: unknown` for fields not enumerated here.
 */
export interface AlarmPipeline {
  name?: string
  description?: string
  enabled?: boolean
  [key: string]: unknown
}

export interface AlarmStatusResult {
  pipelines: AlarmPipeline[]
}

async function requestAlarmStatus(
  p: AlarmStatusParams,
  signal: AbortSignal
): Promise<AlarmStatusResult> {
  const raw = (await requestJson(
    buildUrl('/live-data/alarms/status', {
      gateway_url: p.gatewayUrl,
      project: p.projectName
    }),
    { method: 'GET' },
    signal
  )) as { pipelines?: unknown } | null
  const rawPipelines = Array.isArray(raw?.pipelines) ? raw.pipelines : []
  const pipelines: AlarmPipeline[] = []
  for (const row of rawPipelines) {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) continue
    pipelines.push(row as AlarmPipeline)
  }
  return { pipelines }
}

interface GatewayStatusParams {
  gatewayUrl: string
  projectName?: string
}

/**
 * Normalized payload for `useGatewayStatus`. The proxy returns the
 * enum-ish string `status: "connected" | "unreachable" | "degraded"`;
 * we surface `isOnline` as a derived boolean so common UI code can
 * skip the string comparison. `gateway` carries whatever metadata
 * the upstream gateway shipped back (version, uptime, project
 * counts, etc.) — keys are camelCase, but vary by deployment, so
 * left untyped here.
 */
export interface GatewayStatusResult {
  status: 'connected' | 'unreachable' | 'degraded' | string
  isOnline: boolean
  gateway?: Record<string, unknown>
  error?: string
}

async function requestGatewayStatus(
  p: GatewayStatusParams,
  signal: AbortSignal
): Promise<GatewayStatusResult> {
  const raw = (await requestJson(
    buildUrl('/live-data/gateway-status', {
      gateway_url: p.gatewayUrl,
      project: p.projectName
    }),
    { method: 'GET' },
    signal
  )) as Record<string, unknown> | null
  const status = typeof raw?.status === 'string' ? raw.status : 'degraded'
  const gateway =
    typeof raw?.gateway === 'object' && raw.gateway !== null && !Array.isArray(raw.gateway)
      ? (raw.gateway as Record<string, unknown>)
      : undefined
  const error = typeof raw?.error === 'string' ? raw.error : undefined
  return { status, isOnline: status === 'connected', gateway, error }
}

/**
 * One registered database connection from the proxy posture registry
 * (`GET /api/v1/ontology/db/connections`). `connectionId` is the graph
 * `:Database` URN — pass it to `useDbQuery({ connectionId })`. Transport
 * / gateway / credentials are resolved server-side.
 */
export interface DbConnection {
  connectionId: string
  name: string
  transport?: string
  engine?: string
  databaseType?: string
  capabilities?: string[]
  [key: string]: unknown
}

/**
 * Normalized payload for `useDbConnections`.
 *
 *   - `connections` // registry order
 *   - `byId`        // keyed on `connectionId` (URN) — prefer this for binds
 *   - `byName`      // keyed on display/JDBC `name` (first wins on collision)
 *   - `count`
 */
export interface DbConnectionsResult {
  connections: DbConnection[]
  byId: Record<string, DbConnection>
  byName: Record<string, DbConnection>
  count: number
}

async function requestDbConnections(
  _p: Record<string, never>,
  signal: AbortSignal
): Promise<DbConnectionsResult> {
  // Registry list = connections the connection-addressed `/query` plane can
  // resolve. No gateway_url — transport is server-side posture.
  const raw = (await requestJson(buildUrl('/db/connections'), { method: 'GET' }, signal)) as {
    connections?: unknown
  } | null
  const rawConnections = Array.isArray(raw?.connections) ? raw.connections : []
  const connections: DbConnection[] = []
  const byId: Record<string, DbConnection> = {}
  const byName: Record<string, DbConnection> = {}
  for (const row of rawConnections) {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) continue
    const conn = row as Record<string, unknown>
    const connectionId =
      typeof conn.connection_id === 'string'
        ? conn.connection_id
        : typeof conn.connectionId === 'string'
          ? conn.connectionId
          : ''
    const name = typeof conn.name === 'string' ? conn.name : ''
    const transport = typeof conn.transport === 'string' ? conn.transport : undefined
    const engine = typeof conn.engine === 'string' ? conn.engine : undefined
    const databaseType =
      typeof conn.database_type === 'string'
        ? conn.database_type
        : typeof conn.databaseType === 'string'
          ? conn.databaseType
          : undefined
    const capabilities = Array.isArray(conn.capabilities)
      ? conn.capabilities.filter((c): c is string => typeof c === 'string')
      : undefined
    const normalized: DbConnection = {
      ...conn,
      connectionId,
      name,
      transport,
      engine,
      databaseType,
      capabilities
    }
    connections.push(normalized)
    if (connectionId !== '') byId[connectionId] = normalized
    if (name !== '' && byName[name] === undefined) byName[name] = normalized
  }
  return { connections, byId, byName, count: connections.length }
}

interface DbQueryParams {
  connectionId: string
  query: string
  args?: unknown[]
}

/**
 * Normalized payload for `useDbQuery`.
 *
 *   - `rows`         // one object per result row. Keys are the SQL column names.
 *   - `columns`      // column names in first-row order (empty if no rows).
 *   - `rowCount`     // rows.length.
 *   - `rowsAffected` // always `null` — the hook is read-only (kept for
 *                    //   backwards compatibility with existing dashboards).
 *   - `raw`          // the proxy's `result` passthrough for shapes that
 *                    //   aren't a row array (rare; keeps oddities reachable).
 */
export interface DbQueryResult {
  rows: Array<Record<string, unknown>>
  columns: string[]
  rowCount: number
  rowsAffected: number | null
  raw: unknown
}

async function requestDbQuery(p: DbQueryParams, signal: AbortSignal): Promise<DbQueryResult> {
  // Dashboards render unattended (page views, `pollMs` ticks, headless
  // screenshots), so this always hits the connection-addressed read plane.
  // Transport/credentials resolve from registry posture; there is no write
  // path in the sandbox runtime. URL + body builders are shared with Plant
  // (`@atbash-labs/ontology-plantview`).
  const raw = (await requestJson(
    buildUrl(buildPlantDbQueryUrl(p.connectionId)),
    {
      method: 'POST',
      body: JSON.stringify(buildPlantDbQueryBody({ query: p.query, args: p.args }, 'read'))
    },
    signal
  )) as { result?: unknown } | null
  const result = raw?.result
  if (Array.isArray(result)) {
    const rows = result.filter(
      (row): row is Record<string, unknown> =>
        typeof row === 'object' && row !== null && !Array.isArray(row)
    )
    const columns = rows.length > 0 ? Object.keys(rows[0]) : []
    return { rows, columns, rowCount: rows.length, rowsAffected: null, raw: result }
  }
  if (typeof result === 'number') {
    // Defensive fallback: this hook is read-only, so the gateway should
    // always return a row set. Surface a numeric affected-row count if one
    // ever comes back rather than dropping it.
    return { rows: [], columns: [], rowCount: 0, rowsAffected: result, raw: result }
  }
  return { rows: [], columns: [], rowCount: 0, rowsAffected: null, raw: result ?? null }
}

interface SoapOperationParams {
  endpointId: string
  operationKey: string
  arguments: Record<string, unknown>
}

export interface SoapOperationResult<T = unknown> {
  endpointId: string
  operationKey: string
  result: T
}

interface SoapEndpointMetadata {
  endpoint_id?: unknown
  contract_hash?: unknown
  enabled_operations?: unknown
}

/**
 * Resolve the endpoint's current contract hash immediately before invoking.
 * Dashboard code supplies only graph-owned identities and structured arguments;
 * connection details, credentials, raw XML, and contract hashes stay hidden.
 */
async function requestSoapOperation<T = unknown>(
  p: SoapOperationParams,
  signal: AbortSignal
): Promise<SoapOperationResult<T>> {
  const listed = (await requestJson(buildUrl('/soap/endpoints'), { method: 'GET' }, signal)) as {
    endpoints?: unknown
  } | null
  const endpoints = Array.isArray(listed?.endpoints)
    ? (listed.endpoints as SoapEndpointMetadata[])
    : []
  const endpoint = endpoints.find((item) => item.endpoint_id === p.endpointId)
  if (!endpoint) {
    throw new Error(`SOAP endpoint is not registered: ${p.endpointId}`)
  }
  const enabledOperations = Array.isArray(endpoint.enabled_operations)
    ? endpoint.enabled_operations
    : []
  if (!enabledOperations.includes(p.operationKey)) {
    throw new Error(`SOAP operation is not enabled: ${p.operationKey}`)
  }
  if (typeof endpoint.contract_hash !== 'string' || endpoint.contract_hash === '') {
    throw new Error(`SOAP endpoint has no current contract: ${p.endpointId}`)
  }

  const raw = (await requestJson(
    buildUrl('/soap/invoke'),
    {
      method: 'POST',
      body: JSON.stringify({
        endpoint_id: p.endpointId,
        contract_hash: endpoint.contract_hash,
        operation_key: p.operationKey,
        arguments: p.arguments
      })
    },
    signal
  )) as { endpoint_id?: unknown; operation_key?: unknown; result?: T } | null

  return {
    endpointId: typeof raw?.endpoint_id === 'string' ? raw.endpoint_id : p.endpointId,
    operationKey: typeof raw?.operation_key === 'string' ? raw.operation_key : p.operationKey,
    result: raw?.result as T
  }
}

interface PowerAnalysisParams {
  configId: string
  operation: 'resolve' | 'evaluate'
}

/**
 * One resolved input in a power-analysis result. Mirrors the proxy's
 * `ResolvedInput`: `value` is the resolved number (or `null` when the
 * source could not be read), `provenance` is a human-readable note on
 * where the value came from, and `error` is set only on a failed
 * resolution. `source` echoes the binding's source block verbatim.
 */
export interface PowerAnalysisInput {
  input: string
  unit?: string
  value: number | null
  provenance?: string
  error?: string
  source?: unknown
}

/**
 * One evaluated metric (only present for the `evaluate` operation).
 * Mirrors the proxy's `MetricResult`. `value` / `limit` / `margin` are
 * `null` when the metric could not be checked (an indeterminate verdict).
 */
export interface PowerAnalysisMetric {
  metric: string
  verdict: string
  value: number | null
  limit: number | null
  unit?: string
  margin: number | null
  rationale?: string
}

/**
 * Normalized payload for `usePowerAnalysis`. Combines the two proxy
 * shapes so a widget can read either operation off the same object:
 *
 *   - `resolve`  → `metrics` is empty, `verdict` is null; read `inputs`.
 *   - `evaluate` → `verdict` + `metrics` are populated; `inputs` carry
 *                  the evidence behind each metric.
 *
 * Indexed lookup helpers mirror the other normalized hook results:
 *
 *   data.byMetric['voltage_thd']?.verdict     // "compliant"
 *   data.byInput['nominal_voltage']?.value    // 480
 *   data.isCompliant                          // verdict === "compliant"
 */
export interface PowerAnalysisResult {
  name: string
  standard: string
  operation: 'resolve' | 'evaluate'
  /** Aggregate verdict for `evaluate`; `null` for `resolve`. */
  verdict: string | null
  /** Convenience for `verdict === 'compliant'`. Always false for `resolve`. */
  isCompliant: boolean
  metrics: PowerAnalysisMetric[]
  byMetric: Record<string, PowerAnalysisMetric>
  inputs: PowerAnalysisInput[]
  byInput: Record<string, PowerAnalysisInput>
  raw: unknown
}

function toNumberOrNull(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

function normalizePowerAnalysisInput(row: unknown): PowerAnalysisInput | null {
  if (typeof row !== 'object' || row === null || Array.isArray(row)) return null
  const r = row as Record<string, unknown>
  const input = typeof r.input === 'string' ? r.input : ''
  if (input === '') return null
  const unit = typeof r.unit === 'string' && r.unit !== '' ? r.unit : undefined
  const provenance =
    typeof r.provenance === 'string' && r.provenance !== '' ? r.provenance : undefined
  const error = typeof r.error === 'string' && r.error !== '' ? r.error : undefined
  return {
    input,
    value: toNumberOrNull(r.value),
    ...(unit ? { unit } : {}),
    ...(provenance ? { provenance } : {}),
    ...(error ? { error } : {}),
    ...('source' in r ? { source: r.source } : {})
  }
}

function normalizePowerAnalysisMetric(row: unknown): PowerAnalysisMetric | null {
  if (typeof row !== 'object' || row === null || Array.isArray(row)) return null
  const r = row as Record<string, unknown>
  const metric = typeof r.metric === 'string' ? r.metric : ''
  if (metric === '') return null
  const unit = typeof r.unit === 'string' && r.unit !== '' ? r.unit : undefined
  const rationale = typeof r.rationale === 'string' && r.rationale !== '' ? r.rationale : undefined
  return {
    metric,
    verdict: typeof r.verdict === 'string' ? r.verdict : 'indeterminate',
    value: toNumberOrNull(r.value),
    limit: toNumberOrNull(r.limit),
    margin: toNumberOrNull(r.margin),
    ...(unit ? { unit } : {}),
    ...(rationale ? { rationale } : {})
  }
}

async function requestPowerAnalysis(
  p: PowerAnalysisParams,
  signal: AbortSignal
): Promise<PowerAnalysisResult> {
  // Read-only: both custom verbs are POST-with-empty-body (the config id
  // rides the path). There is NO config-write / mutate path in the sandbox
  // runtime, so a dashboard can never author or change a config on
  // view / pollMs / headless screenshot — matching the DB read/write split.
  const raw = (await requestJson(
    buildUrl(`/power-analysis/configs/${encodeURIComponent(p.configId)}:${p.operation}`),
    { method: 'POST', body: JSON.stringify({}) },
    signal
  )) as Record<string, unknown> | null
  const name = typeof raw?.name === 'string' ? raw.name : ''
  const standard = typeof raw?.standard === 'string' ? raw.standard : ''
  const verdict = typeof raw?.verdict === 'string' ? raw.verdict : null
  const metrics: PowerAnalysisMetric[] = []
  const byMetric: Record<string, PowerAnalysisMetric> = {}
  const rawMetrics = Array.isArray(raw?.metrics) ? raw.metrics : []
  for (const row of rawMetrics) {
    const normalized = normalizePowerAnalysisMetric(row)
    if (!normalized) continue
    metrics.push(normalized)
    byMetric[normalized.metric] = normalized
  }
  const inputs: PowerAnalysisInput[] = []
  const byInput: Record<string, PowerAnalysisInput> = {}
  const rawInputs = Array.isArray(raw?.inputs) ? raw.inputs : []
  for (const row of rawInputs) {
    const normalized = normalizePowerAnalysisInput(row)
    if (!normalized) continue
    inputs.push(normalized)
    byInput[normalized.input] = normalized
  }
  return {
    name,
    standard,
    operation: p.operation,
    verdict,
    isCompliant: verdict === 'compliant',
    metrics,
    byMetric,
    inputs,
    byInput,
    raw
  }
}

interface AgentResultsParams {
  name: string
  lastN?: number
  startTime?: string
  endTime?: string
}

/** One custom-agent result row: `result` conforms to the agent's output schema. */
export interface AgentResultRow {
  /** RFC 3339 timestamp of the run that produced this result. */
  time: string
  runId: string
  result: Record<string, unknown>
}

/**
 * Normalized payload for `useAgentResults`. `results` is newest-first (the
 * proxy's default ordering); any aggregation / reshaping is the dashboard's
 * job — this mirrors the agent-side `read_custom_agent_results` tool 1:1.
 */
export interface AgentResultsResult {
  name: string
  results: AgentResultRow[]
  /** True when more rows exist beyond the returned page. */
  truncated: boolean
}

export type AgentFindingApprovalStatus = 'awaiting_review' | 'approved' | 'rejected'

/** One shredded custom-agent finding (one emit field of one run). */
export interface AgentFinding {
  id: string
  itemId?: string
  runId: string
  time: string
  type: string
  position: number
  result: unknown
  approvalStatus: AgentFindingApprovalStatus
  workStatus?: 'open' | 'done' | null
  feedback?: string
  reviewedAt?: string
}

/**
 * Normalized payload for `useAgentFindings`. `findings` is newest-first
 * (proxy order: time DESC, type ASC, position ASC). A page boundary can
 * split a run; callers that act on a run should use the ids they hold.
 */
export interface AgentFindingsResult {
  name: string
  configId: string
  findings: AgentFinding[]
  /**
   * Top-level output-schema property names that are findings (`x-reviewable`
   * is not `false`). `null` when the config has no schema to read; `[]`
   * when the schema says nothing is a finding.
   */
  reviewableTypes: string[] | null
  /** True when more rows exist beyond the returned page. */
  truncated: boolean
}

const AGENT_RESULTS_DEFAULT_LIMIT = 20
const AGENT_RESULTS_MAX_LIMIT = 500

interface CustomAgentConfigRow {
  id: string
  name: string
  outputSchema: unknown
}

function agentPageSize(lastN: number | undefined): number {
  return Math.min(
    Math.max(Math.floor(lastN ?? AGENT_RESULTS_DEFAULT_LIMIT), 1),
    AGENT_RESULTS_MAX_LIMIT
  )
}

async function resolveCustomAgentConfigByName(
  name: string,
  signal: AbortSignal
): Promise<CustomAgentConfigRow> {
  // Dashboards bind by name; the results route is keyed by config id.
  // The filter expression is interpolated exactly as the existing
  // name-lookup path — do not change quoting.
  const configsRaw = (await requestJson(
    buildV1Url('/custom-agents/configs', { filter: `name="${name}"` }),
    { method: 'GET' },
    signal
  )) as { custom_agent_configs?: unknown } | null
  const configs = Array.isArray(configsRaw?.custom_agent_configs)
    ? configsRaw.custom_agent_configs
    : []
  const match = configs.find(
    (c): c is Record<string, unknown> =>
      typeof c === 'object' &&
      c !== null &&
      (c as Record<string, unknown>).name === name &&
      typeof (c as Record<string, unknown>).id === 'string'
  )
  if (!match) {
    throw new Error(`No custom agent named ${JSON.stringify(name)}.`)
  }
  return {
    id: match.id as string,
    name,
    outputSchema: 'output_schema' in match ? match.output_schema : match.outputSchema
  }
}

/**
 * Top-level property names not marked `x-reviewable: false`. Absent
 * annotation means reviewable. `null` when there is no object schema.
 */
function reviewableTypesFromOutputSchema(outputSchema: unknown): string[] | null {
  if (typeof outputSchema !== 'object' || outputSchema === null || Array.isArray(outputSchema)) {
    return null
  }
  const properties = (outputSchema as Record<string, unknown>).properties
  if (typeof properties !== 'object' || properties === null || Array.isArray(properties)) {
    return null
  }
  const types: string[] = []
  for (const [key, raw] of Object.entries(properties)) {
    if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
      if ((raw as Record<string, unknown>)['x-reviewable'] === false) continue
    }
    types.push(key)
  }
  return types
}

function asApprovalStatus(raw: unknown): AgentFindingApprovalStatus {
  if (raw === 'approved' || raw === 'rejected' || raw === 'awaiting_review') return raw
  return 'awaiting_review'
}

function asOptionalString(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw !== '' ? raw : undefined
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Item-view rows are already shredded by the proxy. Arrays therefore carry
 * one element per row (not the whole array), and scalar values are wrapped in
 * `{ value: ... }` so the public finding result stays object-shaped.
 */
function normalizeAgentFindingResult(
  result: unknown,
  propertySchema: unknown,
  path: string
): unknown {
  if (!isPlainObject(propertySchema)) return result
  const itemSchema =
    (propertySchema.type === 'array' ||
      (Array.isArray(propertySchema.type) && propertySchema.type.includes('array'))) &&
    isPlainObject(propertySchema.items)
      ? propertySchema.items
      : propertySchema
  const itemIsObject =
    itemSchema.type === 'object' ||
    (Array.isArray(itemSchema.type) && itemSchema.type.includes('object')) ||
    isPlainObject(itemSchema.properties)

  if (!itemIsObject && isPlainObject(result) && Object.hasOwn(result, 'value')) {
    return {
      ...result,
      value: normalizeJsonSchemaDateTimes(result.value, itemSchema, `${path}.value`)
    }
  }
  return normalizeJsonSchemaDateTimes(result, itemSchema, path)
}

function normalizeAgentFinding(row: unknown, outputSchema: unknown): AgentFinding | null {
  if (typeof row !== 'object' || row === null || Array.isArray(row)) return null
  const r = row as Record<string, unknown>
  const id = typeof r.id === 'string' ? r.id : ''
  if (id === '') return null
  const runId =
    (typeof r.run_id === 'string' && r.run_id) || (typeof r.runId === 'string' && r.runId) || ''
  const type = typeof r.type === 'string' ? r.type : ''
  const time = normalizeUtcIsoTimestamp(r.time, `custom agent finding ${id} time`, {
    epochUnit: 'milliseconds'
  })
  const position = typeof r.position === 'number' && Number.isFinite(r.position) ? r.position : 0
  const properties =
    isPlainObject(outputSchema) && isPlainObject(outputSchema.properties)
      ? outputSchema.properties
      : {}
  const finding: AgentFinding = {
    id,
    runId,
    time,
    type,
    position,
    result: normalizeAgentFindingResult(
      r.result,
      properties[type],
      `custom agent finding ${id} result`
    ),
    approvalStatus: asApprovalStatus(r.approval_status ?? r.approvalStatus)
  }
  const itemId = typeof r.item_id === 'string' && r.item_id ? r.item_id : undefined
  if (itemId) finding.itemId = itemId
  const workStatus =
    r.work_status === 'open' || r.work_status === 'done' ? r.work_status : undefined
  if (workStatus) finding.workStatus = workStatus
  const feedback = asOptionalString(r.feedback)
  if (feedback) finding.feedback = feedback
  const rawReviewedAt = r.reviewed_at ?? r.reviewedAt
  if (rawReviewedAt !== undefined && rawReviewedAt !== null && rawReviewedAt !== '') {
    finding.reviewedAt = normalizeUtcIsoTimestamp(
      rawReviewedAt,
      `custom agent finding ${id} reviewedAt`,
      { epochUnit: 'milliseconds' }
    )
  }
  return finding
}

async function requestAgentResults(
  p: AgentResultsParams,
  signal: AbortSignal
): Promise<AgentResultsResult> {
  const config = await resolveCustomAgentConfigByName(p.name, signal)
  const pageSize = agentPageSize(p.lastN)
  // Assembled emit documents (one row per run). Proxy default is shredded
  // items — dashboards need the schema-shaped document view.
  const raw = (await requestJson(
    buildV1Url(`/custom-agents/configs/${encodeURIComponent(config.id)}/results`, {
      view: 'document',
      page_size: pageSize,
      start_time: p.startTime,
      end_time: p.endTime
    }),
    { method: 'GET' },
    signal
  )) as { results?: unknown; next_page_token?: unknown } | null

  const rows: AgentResultRow[] = []
  const rawRows = Array.isArray(raw?.results) ? raw.results : []
  for (const row of rawRows) {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) continue
    const r = row as Record<string, unknown>
    const time = normalizeUtcIsoTimestamp(r.time, `custom agent result ${rows.length} time`, {
      epochUnit: 'milliseconds'
    })
    const runId =
      (typeof r.run_id === 'string' && r.run_id) || (typeof r.runId === 'string' && r.runId) || ''
    const result =
      typeof r.result === 'object' && r.result !== null && !Array.isArray(r.result)
        ? (r.result as Record<string, unknown>)
        : {}
    rows.push({
      time,
      runId,
      result: normalizeJsonSchemaDateTimes(
        result,
        config.outputSchema,
        `custom agent result ${rows.length} result`
      ) as Record<string, unknown>
    })
  }
  const truncated = typeof raw?.next_page_token === 'string' && raw.next_page_token !== ''
  return { name: p.name, results: rows, truncated }
}

interface AgentFindingsParams {
  name: string
  type?: string
  approvalStatus?: AgentFindingApprovalStatus
  lastN?: number
  startTime?: string
  endTime?: string
}

async function requestAgentFindings(
  p: AgentFindingsParams,
  signal: AbortSignal
): Promise<AgentFindingsResult> {
  const config = await resolveCustomAgentConfigByName(p.name, signal)
  const pageSize = agentPageSize(p.lastN)
  const raw = (await requestJson(
    buildV1Url(`/custom-agents/configs/${encodeURIComponent(config.id)}/results`, {
      view: 'items',
      type: p.type,
      approval_status: p.approvalStatus,
      page_size: pageSize,
      start_time: p.startTime,
      end_time: p.endTime
    }),
    { method: 'GET' },
    signal
  )) as { results?: unknown; next_page_token?: unknown } | null

  const findings: AgentFinding[] = []
  const rawRows = Array.isArray(raw?.results) ? raw.results : []
  for (const row of rawRows) {
    const finding = normalizeAgentFinding(row, config.outputSchema)
    if (finding) findings.push(finding)
  }
  const truncated = typeof raw?.next_page_token === 'string' && raw.next_page_token !== ''
  return {
    name: p.name,
    configId: config.id,
    findings,
    reviewableTypes: reviewableTypesFromOutputSchema(config.outputSchema),
    truncated
  }
}

/* ----------------------------------- Hooks ----------------------------------- */

/**
 * Manual tag write mutation for explicit operator controls.
 *
 * The hook is inert during render. Call `write` from a click/submit handler;
 * proxy RBAC remains authoritative for whether the operator may write.
 */
export function useTagWrite<T = unknown>(): TagWriteState<T> {
  const [state, setState] = useState<Omit<TagWriteState<T>, 'write'>>({
    pending: false,
    error: null,
    result: null
  })
  const latestInvocation = useRef(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const write = useCallback(async (input: TagWriteInput): Promise<T> => {
    const invocation = latestInvocation.current + 1
    latestInvocation.current = invocation
    setState((previous) => ({ ...previous, pending: true, error: null }))
    try {
      const result = (await writeTagValue(input)) as T
      if (mounted.current && latestInvocation.current === invocation) {
        setState({ pending: false, error: null, result })
      }
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      if (mounted.current && latestInvocation.current === invocation) {
        setState((previous) => ({ ...previous, pending: false, error: message }))
      }
      throw err
    }
  }, [])

  return { write, ...state }
}

export interface DbWriteInput {
  /** Slug of a writeback tool bound to this dashboard (`db_write_<name>` suffix). */
  name: string
  /** Named bind values for the frozen query. */
  params?: Record<string, unknown>
  /** Stable key for this operator click. Generated when omitted. */
  idempotencyKey?: string
}

export interface DbWriteState<T> {
  write: (input: DbWriteInput) => Promise<T>
  pending: boolean
  error: string | null
  result: T | null
}

/**
 * Manual database write for an explicit operator control.
 *
 * The hook is inert during render. `write` posts the tool name and bind
 * values; the proxy executes the SQL approved for this dashboard.
 */
export function useDbWrite<T = unknown>(): DbWriteState<T> {
  const [state, setState] = useState<Omit<DbWriteState<T>, 'write'>>({
    pending: false,
    error: null,
    result: null
  })
  const latestInvocation = useRef(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const write = useCallback(async (input: DbWriteInput): Promise<T> => {
    const invocation = latestInvocation.current + 1
    latestInvocation.current = invocation
    setState((previous) => ({ ...previous, pending: true, error: null }))
    try {
      const result = (await writeDbValue(input)) as T
      if (mounted.current && latestInvocation.current === invocation) {
        setState({ pending: false, error: null, result })
      }
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      if (mounted.current && latestInvocation.current === invocation) {
        setState((previous) => ({ ...previous, pending: false, error: message }))
      }
      throw err
    }
  }, [])

  return { write, ...state }
}

export interface UseTagValuesOptions {
  /** Graph node IDs (URNs) returned by ontology queries and the tag picker. */
  nodeIds: string[] | string
  /** Force an upstream read instead of the cached-first path. */
  fresh?: boolean
  /** Override the server's per-tag freshness budget in milliseconds. */
  maxStalenessMs?: number
  pollMs?: number
}

/**
 * Current values for one or more registered tags, addressed by graph node ID.
 *
 * Node-ID reads expose `data.byNodeId[id]` with value, quality, staleness,
 * acquisition timestamp, source metadata, and the resolved provider tag path.
 */
export function useTagValues<T = TagValuesResult>(options: UseTagValuesOptions): HookState<T> {
  const list = useMemo(() => normalizeNodeIds(options.nodeIds, 'useTagValues'), [options.nodeIds])
  const params = useMemo<TagReadByIdParams>(
    () => ({
      nodeIds: list,
      fresh: options.fresh,
      maxStalenessMs: options.maxStalenessMs
    }),
    [list, options.fresh, options.maxStalenessMs]
  )
  return useDataQuery<T, TagReadByIdParams>({
    request: requestTagReadById as unknown as (
      params: TagReadByIdParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: stableKey(params),
    pollMs: options.pollMs,
    enabled: list.length > 0
  })
}

export interface UseTagHistoryOptions {
  nodeIds: string[] | string
  startDate: string
  endDate: string
  aggregationMode?: string
  returnSize?: number
  intervalMinutes?: number
  aggregateWindow?: boolean
  pollMs?: number
}

/** History bounds within this resolution share a fetch key. */
const TAG_HISTORY_KEY_RESOLUTION_MS = 10_000

/**
 * Fetch key for a history read. Bounds are rounded down to
 * {@link TAG_HISTORY_KEY_RESOLUTION_MS}, so a widget that computes its
 * window inline with `new Date()` aborts and refetches at most once per
 * resolution instead of on every render. The request still sends the
 * latest bounds.
 */
export function tagHistoryParamsKey(params: TagHistoryByIdParams): string {
  const round = (bound: string) => {
    const ms = Date.parse(bound)
    return Number.isNaN(ms)
      ? bound
      : String(Math.floor(ms / TAG_HISTORY_KEY_RESOLUTION_MS) * TAG_HISTORY_KEY_RESOLUTION_MS)
  }
  return stableKey({
    ...params,
    startDate: round(params.startDate),
    endDate: round(params.endDate)
  })
}

/**
 * Historian time-series for registered tags, addressed by graph node ID.
 * Node-ID reads expose tall-form points through `data.byNodeId[id]`.
 */
export function useTagHistory<T = TagHistoryResult>(options: UseTagHistoryOptions): HookState<T> {
  const list = useMemo(() => normalizeNodeIds(options.nodeIds, 'useTagHistory'), [options.nodeIds])
  const params = useMemo<TagHistoryByIdParams>(
    () => ({
      nodeIds: list,
      startDate: options.startDate,
      endDate: options.endDate,
      aggregationMode: options.aggregationMode,
      returnSize: options.returnSize,
      intervalMinutes: options.intervalMinutes,
      aggregateWindow: options.aggregateWindow
    }),
    [
      list,
      options.startDate,
      options.endDate,
      options.aggregationMode,
      options.returnSize,
      options.intervalMinutes,
      options.aggregateWindow
    ]
  )
  return useDataQuery<T, TagHistoryByIdParams>({
    request: requestTagHistoryById as unknown as (
      params: TagHistoryByIdParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: tagHistoryParamsKey(params),
    pollMs: options.pollMs,
    enabled: list.length > 0
  })
}

export interface UseCypherQueryOptions {
  /** Read-only Cypher. Mutating clauses are rejected by the proxy. */
  query: string
  /** Named values for `$parameter` references in the query. */
  params?: Record<string, unknown>
  pollMs?: number
}

/**
 * Run a branch/site-scoped, read-only ontology query.
 *
 * Returns the first proxy page and explicit completeness metadata. Keep
 * dashboard queries bounded and aggregate in Cypher rather than returning
 * whole subgraphs.
 */
export function useCypherQuery<T = CypherQueryResult>(
  options: UseCypherQueryOptions
): HookState<T> {
  const params = useMemo<CypherQueryParams>(
    () => ({ query: options.query, params: options.params }),
    [options.query, options.params]
  )
  return useDataQuery<T, CypherQueryParams>({
    request: requestCypherQuery as unknown as (
      params: CypherQueryParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: `${options.query}\n${JSON.stringify(options.params ?? {})}`,
    pollMs: options.pollMs,
    enabled: options.query.trim().length > 0
  })
}

/** Epoch-millisecond compatibility hook selected only for unmarked saved code. */
export function useLegacyTagHistory(
  options: UseTagHistoryOptions
): HookState<LegacyTagHistoryResult> {
  const state = useTagHistory<TagHistoryResult>(options)
  const data = useMemo(
    () => (state.data === null ? null : toLegacyTagHistoryResult(state.data)),
    [state.data]
  )
  return useMemo(
    () => ({ data, error: state.error, loading: state.loading }),
    [data, state.error, state.loading]
  )
}

export interface UseActiveAlarmsOptions {
  gatewayUrl: string
  projectName?: string
  priority?: string
  state?: string
  pollMs?: number
}

/**
 * Currently-active alarms on a gateway.
 *
 * Returns `data: ActiveAlarmsResult`:
 *
 *   data.alarms       // ActiveAlarm[] in upstream order
 *   data.byPriority   // Record<priority, ActiveAlarm[]>
 *   data.byState      // Record<state, ActiveAlarm[]>
 *
 * Filter at the call site with `priority` / `state` options to push
 * the filter to the gateway, or filter client-side with the indexes
 * above when you need multiple slices of the same fetch.
 */
export function useActiveAlarms<T = ActiveAlarmsResult>(
  options: UseActiveAlarmsOptions
): HookState<T> {
  const params = useMemo<ActiveAlarmsParams>(
    () => ({
      gatewayUrl: options.gatewayUrl,
      projectName: options.projectName,
      priority: options.priority,
      state: options.state
    }),
    [options.gatewayUrl, options.projectName, options.priority, options.state]
  )
  return useDataQuery<T, ActiveAlarmsParams>({
    request: requestActiveAlarms as unknown as (
      params: ActiveAlarmsParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: stableKey(params),
    pollMs: options.pollMs,
    enabled: options.gatewayUrl.length > 0
  })
}

export interface UseAlarmStatusOptions {
  gatewayUrl: string
  projectName?: string
  pollMs?: number
}

/**
 * Alarm-pipeline configuration on a gateway.
 *
 * Returns `data: AlarmStatusResult` where `data.pipelines` is an
 * array of `AlarmPipeline` objects (camelCase keys, project-specific
 * fields preserved via the `[key: string]: unknown` index).
 */
export function useAlarmStatus<T = AlarmStatusResult>(
  options: UseAlarmStatusOptions
): HookState<T> {
  const params = useMemo<AlarmStatusParams>(
    () => ({ gatewayUrl: options.gatewayUrl, projectName: options.projectName }),
    [options.gatewayUrl, options.projectName]
  )
  return useDataQuery<T, AlarmStatusParams>({
    request: requestAlarmStatus as unknown as (
      params: AlarmStatusParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: stableKey(params),
    pollMs: options.pollMs,
    enabled: options.gatewayUrl.length > 0
  })
}

export interface UseGatewayStatusOptions {
  gatewayUrl: string
  projectName?: string
  pollMs?: number
}

/**
 * Online/offline + uptime + version info for a gateway.
 *
 * Returns `data: GatewayStatusResult`:
 *
 *   data.status     // "connected" | "unreachable" | "degraded"
 *   data.isOnline   // boolean (derived from status === "connected")
 *   data.gateway    // Record<string, unknown> — version/uptime/etc.
 *                   //   when reachable; undefined otherwise.
 *   data.error      // human-readable error string for non-connected.
 */
export function useGatewayStatus<T = GatewayStatusResult>(
  options: UseGatewayStatusOptions
): HookState<T> {
  const params = useMemo<GatewayStatusParams>(
    () => ({ gatewayUrl: options.gatewayUrl, projectName: options.projectName }),
    [options.gatewayUrl, options.projectName]
  )
  return useDataQuery<T, GatewayStatusParams>({
    request: requestGatewayStatus as unknown as (
      params: GatewayStatusParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: stableKey(params),
    pollMs: options.pollMs,
    enabled: options.gatewayUrl.length > 0
  })
}

export interface UseDbConnectionsOptions {
  pollMs?: number
}

/**
 * Registered database connections from the proxy posture registry
 * (`GET /api/v1/ontology/db/connections`). No gateway URL — identity is
 * the graph `:Database` URN (`connectionId`); transport is server-resolved.
 *
 * Returns `data: DbConnectionsResult`:
 *
 *   data.connections   // DbConnection[] in registry order
 *   data.byId          // Record<connectionId, DbConnection> — prefer for binds
 *   data.byName        // Record<name, DbConnection>
 *   data.count
 *
 * Pass `connectionId` to `useDbQuery`.
 */
export function useDbConnections<T = DbConnectionsResult>(
  options: UseDbConnectionsOptions = {}
): HookState<T> {
  const params = useMemo(() => ({}), [])
  return useDataQuery<T, Record<string, never>>({
    request: requestDbConnections as unknown as (
      params: Record<string, never>,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: 'db-connections',
    pollMs: options.pollMs,
    enabled: true
  })
}

export interface UseDbQueryOptions {
  /** Graph `:Database` URN from `useDbConnections` / `list_db_connections`. */
  connectionId: string
  query: string
  args?: unknown[]
  pollMs?: number
  /**
   * Optional delay before the first fetch. When omitted, the sandbox
   * auto-staggers mounts: every other `useDbQuery` waits
   * {@link DB_QUERY_STAGGER_MS} so roughly half the dashboard's DB
   * queries fire in a second wave (avoids Ignition "too many concurrent
   * queries" on multi-hook dashboards).
   */
  delayMs?: number
  /** Delay before retrying a failed query (default 5000). `0` disables. */
  retryMs?: number
  /** Retries after the first failure (default 8). */
  maxRetries?: number
}

/**
 * Run a read-only SQL query against a registered database connection
 * (`POST /api/v1/ontology/db/connections/{id}/query`). Transport and
 * credentials resolve from registry posture server-side.
 *
 * Returns `data: DbQueryResult`:
 *
 *   data.rows          // Array<Record<column, value>>
 *   data.columns       // column names (first-row order)
 *   data.rowCount      // rows.length
 *   data.rowsAffected  // always null (this hook is read-only)
 *
 * The hook no-ops until `connectionId` and `query` are both non-empty.
 * Prefer parameterized queries via `args` over string interpolation.
 * A `pollMs` re-runs the query on an interval.
 *
 * Concurrent mounts are auto-staggered (odd hooks delayed ~1.5s) unless
 * you pass an explicit `delayMs`. Failed fetches retry after 5s (up to 8
 * times) by default — override with `retryMs` / `maxRetries`.
 */
export function useDbQuery<T = DbQueryResult>(options: UseDbQueryOptions): HookState<T> {
  const params = useMemo<DbQueryParams>(
    () => ({
      connectionId: options.connectionId,
      query: options.query,
      args: options.args
    }),
    [options.connectionId, options.query, options.args]
  )
  // Capture once per hook instance so remounts of the parent don't reshuffle
  // which queries land in wave 1 vs wave 2.
  const [autoDelayMs] = useState(() => {
    if (typeof options.delayMs === 'number') return Math.max(0, options.delayMs)
    const slot = dbQueryMountSeq++
    return slot % 2 === 1 ? DB_QUERY_STAGGER_MS : 0
  })
  const delayMs = typeof options.delayMs === 'number' ? Math.max(0, options.delayMs) : autoDelayMs

  return useDataQuery<T, DbQueryParams>({
    request: requestDbQuery as unknown as (
      params: DbQueryParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: stableKey(params),
    pollMs: options.pollMs,
    delayMs,
    // Ride out Ignition concurrent-query / transient gateway errors.
    retryMs: typeof options.retryMs === 'number' ? Math.max(0, options.retryMs) : 5000,
    maxRetries: typeof options.maxRetries === 'number' ? Math.max(0, options.maxRetries) : 8,
    enabled: options.connectionId.trim().length > 0 && options.query.trim().length > 0
  })
}

export interface UseSoapOperationOptions {
  /** Graph SOAP endpoint URN returned by `list_soap_endpoints`. */
  endpointId: string
  /** Enabled read operation key returned by `list_soap_endpoints`. */
  operationKey: string
  /** Structured arguments matching `describe_soap_operation`. */
  arguments: Record<string, unknown>
  pollMs?: number
}

/**
 * Invoke a graph-registered, agent-enabled SOAP read operation.
 *
 * Returns `data: { endpointId, operationKey, result }`. `result` is the
 * operation's JSON response and can be bound directly to cards, tables, and
 * charts. The current contract hash is resolved from the proxy registry on
 * each request, so saved dashboards do not persist transport metadata.
 */
export function useSoapOperation<T = unknown>(
  options: UseSoapOperationOptions
): HookState<SoapOperationResult<T>> {
  const params = useMemo<SoapOperationParams>(
    () => ({
      endpointId: options.endpointId,
      operationKey: options.operationKey,
      arguments: options.arguments
    }),
    [options.endpointId, options.operationKey, options.arguments]
  )
  return useDataQuery<SoapOperationResult<T>, SoapOperationParams>({
    request: requestSoapOperation<T>,
    params,
    paramsKey: `${options.endpointId}\n${options.operationKey}\n${JSON.stringify(options.arguments)}`,
    pollMs: options.pollMs,
    enabled: options.endpointId.trim().length > 0 && options.operationKey.trim().length > 0
  })
}

export interface UsePowerAnalysisOptions {
  /** The power-analysis config id (UUID) to resolve or evaluate. */
  configId: string
  /**
   * `evaluate` (default) runs the config's standard evaluator and returns
   * per-metric compliance verdicts. `resolve` only fetches each binding's
   * live value without evaluating it — useful for a "current readings"
   * widget that shows the raw inputs behind a compliance check.
   */
  operation?: 'resolve' | 'evaluate'
  pollMs?: number
}

/**
 * Resolve or evaluate a saved power-analysis config against its standard
 * (e.g. IEEE 519-2022). The config's bindings pull live values from tags,
 * Neo4j node properties, and constants server-side, so a dashboard needs
 * only the `configId` — the gateway, tag paths, and thresholds live in the
 * config, not the widget.
 *
 * Returns `data: PowerAnalysisResult`:
 *
 *   data.verdict      // "compliant" | "non_compliant" | "indeterminate"
 *                     //   (null for the `resolve` operation)
 *   data.isCompliant  // verdict === "compliant"
 *   data.metrics      // PowerAnalysisMetric[] (empty for `resolve`)
 *   data.byMetric     // Record<metric, PowerAnalysisMetric> — fast lookup
 *   data.inputs       // PowerAnalysisInput[] — resolved evidence values
 *   data.byInput      // Record<input, PowerAnalysisInput> — fast lookup
 *
 * Read-only: neither operation mutates the config or any state, so it is
 * safe on page view, `pollMs` refresh, and headless screenshots.
 */
export function usePowerAnalysis<T = PowerAnalysisResult>(
  options: UsePowerAnalysisOptions
): HookState<T> {
  const operation = options.operation ?? 'evaluate'
  const params = useMemo<PowerAnalysisParams>(
    () => ({ configId: options.configId, operation }),
    [options.configId, operation]
  )
  return useDataQuery<T, PowerAnalysisParams>({
    request: requestPowerAnalysis as unknown as (
      params: PowerAnalysisParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: stableKey(params),
    pollMs: options.pollMs,
    enabled: options.configId.length > 0
  })
}

export interface UseAgentResultsOptions {
  /** The custom agent's unique name (as shown on the Custom Agents page). */
  name: string
  /** Most recent N results (default 20, capped at 500). Ignored when a window is set. */
  lastN?: number
  /** Optional RFC 3339 window start (inclusive). */
  startTime?: string
  /** Optional RFC 3339 window end (exclusive). */
  endTime?: string
  pollMs?: number
}

/**
 * Timeseries results produced by a custom agent (the user-defined
 * prompt + output-schema agents on the Configure page). Each run appends one
 * result document conforming to the agent's JSON output schema.
 *
 * Returns `data: AgentResultsResult`:
 *
 *   data.results     // AgentResultRow[] newest-first: { time, runId, result }
 *   data.results[0]?.result   // the latest result document
 *   data.truncated   // true when more rows exist beyond the page
 *
 * Mirrors the agent-side `read_custom_agent_results` tool 1:1 — pass `lastN`
 * for the most recent N rows, or `startTime`/`endTime` for a window. The
 * result documents' field names come from the agent's output schema (use
 * `read_custom_agent_schema` / the Configure page to see it). Do any
 * aggregation client-side from the raw rows.
 */
export function useAgentResults<T = AgentResultsResult>(
  options: UseAgentResultsOptions
): HookState<T> {
  const params = useMemo<AgentResultsParams>(
    () => ({
      name: options.name,
      lastN: options.lastN,
      startTime: options.startTime,
      endTime: options.endTime
    }),
    [options.name, options.lastN, options.startTime, options.endTime]
  )
  return useDataQuery<T, AgentResultsParams>({
    request: requestAgentResults as unknown as (
      params: AgentResultsParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: stableKey(params),
    pollMs: options.pollMs,
    enabled: options.name.length > 0
  })
}

export interface UseAgentFindingsOptions {
  /** The custom agent's unique name (as shown on the Custom Agents page). */
  name: string
  /** Shredded result type (top-level output-schema property name). */
  type?: string
  approvalStatus?: AgentFindingApprovalStatus
  /** Most recent N findings (default 20, capped at 500). */
  lastN?: number
  /** Optional RFC 3339 window start (inclusive). */
  startTime?: string
  /** Optional RFC 3339 window end (exclusive). */
  endTime?: string
  pollMs?: number
}

/**
 * Shredded findings produced by a custom agent — one row per emit field,
 * with `id` and `approvalStatus`. Charts and KPI history should keep using
 * {@link useAgentResults} (assembled documents, no review state).
 *
 * Returns `data: AgentFindingsResult`:
 *
 *   data.findings           // AgentFinding[] newest-first
 *   data.configId           // config UUID from the name lookup
 *   data.reviewableTypes    // schema properties that are findings, or null
 *   data.truncated          // true when more rows exist beyond the page
 *
 * Subscribes to {@link bumpDataRevision} on {@link AGENT_FINDINGS_REVISION_SCOPE}.
 * Call that only from event handlers — a bump during render refetches forever.
 */
export function useAgentFindings<T = AgentFindingsResult>(
  options: UseAgentFindingsOptions
): HookState<T> {
  const params = useMemo<AgentFindingsParams>(
    () => ({
      name: options.name,
      type: options.type,
      approvalStatus: options.approvalStatus,
      lastN: options.lastN,
      startTime: options.startTime,
      endTime: options.endTime
    }),
    [
      options.name,
      options.type,
      options.approvalStatus,
      options.lastN,
      options.startTime,
      options.endTime
    ]
  )
  return useDataQuery<T, AgentFindingsParams>({
    request: requestAgentFindings as unknown as (
      params: AgentFindingsParams,
      signal: AbortSignal
    ) => Promise<unknown>,
    params,
    paramsKey: stableKey(params),
    pollMs: options.pollMs,
    enabled: options.name.length > 0,
    revisionScope: AGENT_FINDINGS_REVISION_SCOPE
  })
}

/* ---------------------- Imperative (non-hook) data functions ---------------------- */
//
// Async twins of the live-data hooks above, for use OUTSIDE of React — i.e.
// the `run_dashboard_script` tool. They share the exact same auth (the API
// key the sandbox received over `sandbox-init`), the same proxy plumbing
// (`buildUrl` / `buildHeaders` / `requestJson`), and the same normalized
// return shapes (`TagValuesResult`, `TagHistoryResult`, …) as the hooks. So
// a transformation validated in a script behaves identically when the same
// data is later read through the corresponding hook in dashboard TSX.
//
// Each function resolves once (no polling); pass a `pollMs` only to the
// hooks. They throw if the sandbox has not yet been initialized with
// credentials, which the script runtime surfaces as the script's error.

function requireAuth(): void {
  if (auth === null) {
    throw new Error(
      'Sandbox is not initialized with credentials yet — data functions are unavailable.'
    )
  }
}

function normalizeNodeIds(nodeIds: string[] | string | null | undefined, caller: string): string[] {
  if (nodeIds === null || nodeIds === undefined) {
    throw new Error(`${caller} requires { nodeIds }; path-based tag reads are not supported.`)
  }
  const normalized = (Array.isArray(nodeIds) ? nodeIds : String(nodeIds).split(','))
    .map((value) => value.trim())
    .filter(Boolean)
  const invalid = normalized.find((nodeId) => !nodeId.startsWith('urn:'))
  if (invalid) {
    throw new Error(`${caller} requires graph URNs in nodeIds; received "${invalid}".`)
  }
  return normalized
}

function orFreshSignal(signal?: AbortSignal): AbortSignal {
  return signal ?? new AbortController().signal
}

/** Async twin of {@link useTagValues}. Resolves to a {@link TagValuesResult}. */
export async function fetchTagValues(
  options: Omit<UseTagValuesOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<TagValuesResult> {
  requireAuth()
  return requestTagReadById(
    {
      nodeIds: normalizeNodeIds(options.nodeIds, 'fetchTagValues'),
      fresh: options.fresh,
      maxStalenessMs: options.maxStalenessMs
    },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useTagHistory}. Resolves to a {@link TagHistoryResult}. */
export async function fetchTagHistory(
  options: Omit<UseTagHistoryOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<TagHistoryResult> {
  requireAuth()
  return requestTagHistoryById(
    {
      nodeIds: normalizeNodeIds(options.nodeIds, 'fetchTagHistory'),
      startDate: options.startDate,
      endDate: options.endDate,
      aggregationMode: options.aggregationMode,
      returnSize: options.returnSize,
      intervalMinutes: options.intervalMinutes,
      aggregateWindow: options.aggregateWindow
    },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useCypherQuery}. Resolves to one read-only result page. */
export async function fetchCypherQuery(
  options: Omit<UseCypherQueryOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<CypherQueryResult> {
  requireAuth()
  const query = options.query?.trim() ?? ''
  if (query === '') {
    throw new Error('fetchCypherQuery requires a non-empty { query }.')
  }
  return requestCypherQuery(
    {
      query,
      params: options.params
    },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useActiveAlarms}. Resolves to an {@link ActiveAlarmsResult}. */
export async function fetchActiveAlarms(
  options: Omit<UseActiveAlarmsOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<ActiveAlarmsResult> {
  requireAuth()
  return requestActiveAlarms(
    {
      gatewayUrl: options.gatewayUrl,
      projectName: options.projectName,
      priority: options.priority,
      state: options.state
    },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useAlarmStatus}. Resolves to an {@link AlarmStatusResult}. */
export async function fetchAlarmStatus(
  options: Omit<UseAlarmStatusOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<AlarmStatusResult> {
  requireAuth()
  return requestAlarmStatus(
    { gatewayUrl: options.gatewayUrl, projectName: options.projectName },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useGatewayStatus}. Resolves to a {@link GatewayStatusResult}. */
export async function fetchGatewayStatus(
  options: Omit<UseGatewayStatusOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<GatewayStatusResult> {
  requireAuth()
  return requestGatewayStatus(
    { gatewayUrl: options.gatewayUrl, projectName: options.projectName },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useDbConnections}. Resolves to a {@link DbConnectionsResult}. */
export async function fetchDbConnections(
  _options: Omit<UseDbConnectionsOptions, 'pollMs'> = {},
  signal?: AbortSignal
): Promise<DbConnectionsResult> {
  requireAuth()
  return requestDbConnections({}, orFreshSignal(signal))
}

/** Async twin of {@link useDbQuery}. Resolves to a {@link DbQueryResult}. */
export async function fetchDbQuery(
  options: Omit<UseDbQueryOptions, 'pollMs' | 'delayMs' | 'retryMs' | 'maxRetries'>,
  signal?: AbortSignal
): Promise<DbQueryResult> {
  requireAuth()
  const connectionId = options.connectionId?.trim() ?? ''
  if (connectionId === '') {
    throw new Error('fetchDbQuery requires { connectionId } (graph :Database URN).')
  }
  if (!(options.query?.trim() ?? '')) {
    throw new Error('fetchDbQuery requires a non-empty { query }.')
  }
  return requestDbQuery(
    {
      connectionId,
      query: options.query,
      args: options.args
    },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useSoapOperation}. Resolves to the operation's JSON result envelope. */
export async function fetchSoapOperation<T = unknown>(
  options: Omit<UseSoapOperationOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<SoapOperationResult<T>> {
  requireAuth()
  const endpointId = options.endpointId?.trim() ?? ''
  const operationKey = options.operationKey?.trim() ?? ''
  if (endpointId === '') {
    throw new Error('fetchSoapOperation requires { endpointId } from list_soap_endpoints.')
  }
  if (operationKey === '') {
    throw new Error('fetchSoapOperation requires { operationKey } from list_soap_endpoints.')
  }
  return requestSoapOperation<T>(
    {
      endpointId,
      operationKey,
      arguments: options.arguments ?? {}
    },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link usePowerAnalysis}. Resolves to a {@link PowerAnalysisResult}. */
export async function fetchPowerAnalysis(
  options: Omit<UsePowerAnalysisOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<PowerAnalysisResult> {
  requireAuth()
  return requestPowerAnalysis(
    { configId: options.configId, operation: options.operation ?? 'evaluate' },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useAgentResults}. Resolves to an {@link AgentResultsResult}. */
export async function fetchAgentResults(
  options: Omit<UseAgentResultsOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<AgentResultsResult> {
  requireAuth()
  return requestAgentResults(
    {
      name: options.name,
      lastN: options.lastN,
      startTime: options.startTime,
      endTime: options.endTime
    },
    orFreshSignal(signal)
  )
}

/** Async twin of {@link useAgentFindings}. Resolves to an {@link AgentFindingsResult}. */
export async function fetchAgentFindings(
  options: Omit<UseAgentFindingsOptions, 'pollMs'>,
  signal?: AbortSignal
): Promise<AgentFindingsResult> {
  requireAuth()
  return requestAgentFindings(
    {
      name: options.name,
      type: options.type,
      approvalStatus: options.approvalStatus,
      lastN: options.lastN,
      startTime: options.startTime,
      endTime: options.endTime
    },
    orFreshSignal(signal)
  )
}

let tagWriteKeySequence = 0

function createTagWriteIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return `dashboard-${globalThis.crypto.randomUUID()}`
  }
  tagWriteKeySequence += 1
  return `dashboard-${Date.now().toString(36)}-${tagWriteKeySequence.toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`
}

function normalizeTagWriteInput(input: TagWriteInput): Required<TagWriteInput> {
  if (typeof input !== 'object' || input === null) {
    throw new Error('writeTagValue requires { targetId, value }.')
  }
  const targetId = typeof input.targetId === 'string' ? input.targetId.trim() : ''
  if (targetId === '') {
    throw new Error('writeTagValue requires a non-empty targetId.')
  }
  const valueType = typeof input.value
  if (
    (valueType !== 'number' && valueType !== 'string' && valueType !== 'boolean') ||
    (valueType === 'number' && !Number.isFinite(input.value))
  ) {
    throw new Error('writeTagValue value must be a finite number, string, or boolean.')
  }
  const idempotencyKey =
    input.idempotencyKey === undefined
      ? createTagWriteIdempotencyKey()
      : typeof input.idempotencyKey === 'string'
        ? input.idempotencyKey.trim()
        : ''
  if (idempotencyKey === '') {
    throw new Error('writeTagValue idempotencyKey must be a non-empty string when provided.')
  }
  return { targetId, value: input.value, idempotencyKey }
}

/**
 * Perform one manual, audited tag write.
 *
 * An omitted idempotency key is generated once before the request starts, so
 * any transport retry of this invocation can reuse the same normalized input.
 * Dashboard code must call this only from an explicit operator action.
 */
export async function writeTagValue<T = unknown>(
  input: TagWriteInput,
  signal?: AbortSignal
): Promise<T> {
  requireAuth()
  const normalized = normalizeTagWriteInput(input)
  return requestJson(
    buildUrl('/writeback/tags:execute'),
    {
      method: 'POST',
      body: JSON.stringify({
        target_id: normalized.targetId,
        value: normalized.value,
        actor_cause: 'human',
        idempotency_key: normalized.idempotencyKey
      })
    },
    orFreshSignal(signal)
  ) as Promise<T>
}

const DB_WRITE_NAME = /^[a-z][a-z0-9_]*$/

function normalizeDbWriteInput(input: DbWriteInput): {
  name: string
  params: Record<string, unknown>
  idempotencyKey: string
} {
  const name = typeof input?.name === 'string' ? input.name.trim() : ''
  if (!DB_WRITE_NAME.test(name) || name.length > 64) {
    throw new Error(
      'writeDbValue name must be the bound tool slug: a lowercase letter, then [a-z0-9_], max 64 chars.'
    )
  }
  if (
    input.params !== undefined &&
    (input.params === null || typeof input.params !== 'object' || Array.isArray(input.params))
  ) {
    throw new Error('writeDbValue params must be an object of named bind values.')
  }
  const idempotencyKey =
    input.idempotencyKey === undefined
      ? createTagWriteIdempotencyKey()
      : typeof input.idempotencyKey === 'string'
        ? input.idempotencyKey.trim()
        : ''
  if (idempotencyKey === '') {
    throw new Error('writeDbValue idempotencyKey must be a non-empty string when provided.')
  }
  return { name, params: input.params ?? {}, idempotencyKey }
}

/**
 * Perform one manual, audited database write through a tool bound to the
 * saved dashboard. Dashboard code must call this only from an explicit
 * operator action. Templates and artifacts have no dashboard id.
 */
export async function writeDbValue<T = unknown>(
  input: DbWriteInput,
  signal?: AbortSignal
): Promise<T> {
  requireAuth()
  const dashboardId = auth?.dashboardId?.trim() ?? ''
  if (dashboardId === '') {
    throw new Error(
      'writeDbValue requires a saved dashboard. Templates, unsaved previews, and chat artifacts cannot write.'
    )
  }
  const normalized = normalizeDbWriteInput(input)
  return requestJson(
    buildV1Url(
      `/dashboards/${encodeURIComponent(dashboardId)}/writeback-tools/${encodeURIComponent(normalized.name)}:execute`
    ),
    {
      method: 'POST',
      body: JSON.stringify({
        params: normalized.params,
        idempotency_key: normalized.idempotencyKey
      })
    },
    orFreshSignal(signal)
  ) as Promise<T>
}

/** Server cap on `feedback`; the denial composer enforces the same limit. */
export const AGENT_FINDING_FEEDBACK_MAX_CHARS = 4096

export interface PatchAgentFindingInput {
  id: string
  approvalStatus: AgentFindingApprovalStatus
  feedback?: string
}

export interface PatchAgentFindingsSettled {
  ok: AgentFinding[]
  failed: Array<{ id: string; error: string }>
}

/**
 * PATCH one shredded result's review state. Used by `<ReviewActions>` —
 * not registered in script scope. Call only from a click handler, then
 * {@link bumpDataRevision} for {@link AGENT_FINDINGS_REVISION_SCOPE}.
 */
export async function patchAgentFinding(
  input: PatchAgentFindingInput,
  signal?: AbortSignal
): Promise<AgentFinding> {
  requireAuth()
  const id = input.id.trim()
  if (id === '') {
    throw new Error('patchAgentFinding requires a result id.')
  }
  let feedback = input.feedback
  if (typeof feedback === 'string') {
    feedback = feedback.trim()
    if (feedback.length > AGENT_FINDING_FEEDBACK_MAX_CHARS) {
      throw new Error(`feedback must be at most ${AGENT_FINDING_FEEDBACK_MAX_CHARS} characters.`)
    }
    if (feedback === '') feedback = undefined
  }
  const raw = await requestJson(
    buildV1Url(`/custom-agents/results/${encodeURIComponent(id)}`),
    {
      method: 'PATCH',
      body: JSON.stringify({
        approval_status: input.approvalStatus,
        ...(feedback !== undefined ? { feedback } : {})
      })
    },
    orFreshSignal(signal)
  )
  const finding = normalizeAgentFinding(raw, undefined)
  if (!finding) {
    throw new Error('Review update returned an unexpected payload.')
  }
  return finding
}

/**
 * PATCH many result ids with {@link Promise.allSettled}. Partial failure
 * does not abort the rest — the caller reports "n of m".
 */
export async function batchAgentFindings(
  input: {
    itemIds: string[]
    approvalStatus?: AgentFindingApprovalStatus
    feedback?: string
    workStatus?: 'open' | 'done'
    startFollowUp?: boolean
  },
  signal?: AbortSignal
): Promise<{ results: AgentFinding[]; activeFollowUpRunId?: string }> {
  requireAuth()
  const itemIds = [...new Set(input.itemIds.map((id) => id.trim()).filter(Boolean))]
  if (itemIds.length === 0) {
    throw new Error('batchAgentFindings requires item ids.')
  }
  if (input.approvalStatus === 'rejected' && !input.feedback?.trim()) {
    throw new Error('feedback is required when rejecting.')
  }
  const raw = await requestJson(
    buildV1Url('/custom-agents/results:batch'),
    {
      method: 'PATCH',
      body: JSON.stringify({
        item_ids: itemIds,
        ...(input.approvalStatus ? { approval_status: input.approvalStatus } : {}),
        ...(input.feedback?.trim() ? { feedback: input.feedback.trim() } : {}),
        ...(input.workStatus ? { work_status: input.workStatus } : {}),
        ...(input.startFollowUp ? { start_follow_up: true } : {})
      })
    },
    orFreshSignal(signal)
  )
  const page = raw as { results?: unknown[]; active_follow_up_run_id?: string } | null
  const results = (page?.results ?? [])
    .map((row) => normalizeAgentFinding(row, undefined))
    .filter((row): row is AgentFinding => row !== null)
  return {
    results,
    ...(typeof page?.active_follow_up_run_id === 'string' && page.active_follow_up_run_id
      ? { activeFollowUpRunId: page.active_follow_up_run_id }
      : {})
  }
}

export async function patchAgentFindingsSettled(
  ids: string[],
  approvalStatus: AgentFindingApprovalStatus,
  feedback?: string,
  signal?: AbortSignal
): Promise<PatchAgentFindingsSettled> {
  const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))]
  const settled = await Promise.allSettled(
    unique.map((id) => patchAgentFinding({ id, approvalStatus, feedback }, signal))
  )
  const ok: AgentFinding[] = []
  const failed: Array<{ id: string; error: string }> = []
  settled.forEach((entry, index) => {
    const id = unique[index] ?? ''
    if (entry.status === 'fulfilled') {
      ok.push(entry.value)
      return
    }
    const error = entry.reason instanceof Error ? entry.reason.message : String(entry.reason)
    failed.push({ id, error })
  })
  return { ok, failed }
}
